import { request, RequestError } from "./api";
import { getToken } from "./auth";
import { track } from "./events";
import type { AbsenceReasonCode } from "../types";

// Durable outbox for attendance marks. Same shape as the usage-event queue
// in events.ts — a JSON array in localStorage, flushed on demand and on
// `online` — but each entry carries its own delivery state so the roster
// can show per-teacher sync status, and gives up under a cap instead of
// retrying forever.
const OUTBOX_KEY = "cerp-attendance-outbox";

export const MAX_ATTEMPTS = 3;
export const MAX_AGE_MS = 60_000;
const RETRY_INTERVAL_MS = 5000;

export type OutboxState = "queued" | "syncing" | "failed";

export interface OutboxEntry {
  id: string;
  sessionID: string;
  teacherID: string;
  teacherName: string;
  status: "present" | "absent";
  reasonCode?: AbsenceReasonCode;
  reasonDetail?: string;
  state: OutboxState;
  attempts: number;
  firstQueuedAt: number;
  generation?: number;
  error?: string;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let cache: OutboxEntry[] | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function read(): OutboxEntry[] {
  if (cache) return cache;
  try {
    cache = JSON.parse(localStorage.getItem(OUTBOX_KEY) || "[]") as OutboxEntry[];
  } catch {
    cache = [];
  }
  return cache;
}

// The cached array is replaced, never mutated, so useSyncExternalStore sees
// a new reference exactly when something actually changed.
function write(entries: OutboxEntry[]): void {
  cache = entries;
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or unavailable: the in-memory cache still drives the UI
    // for this session.
  }
  listeners.forEach((listener) => listener());
}

export function subscribeOutbox(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function outboxSnapshot(): OutboxEntry[] {
  return read();
}

export function outboxFor(sessionID: string): OutboxEntry[] {
  return read().filter((entry) => entry.sessionID === sessionID);
}

// Capped by attempts or age, whichever trips first.
export function isCapped(entry: OutboxEntry): boolean {
  return entry.attempts >= MAX_ATTEMPTS || Date.now() - entry.firstQueuedAt >= MAX_AGE_MS;
}

export interface QueueInput {
  sessionID: string;
  teacherID: string;
  teacherName: string;
  status: "present" | "absent";
  reasonCode?: AbsenceReasonCode;
  reasonDetail?: string;
}

// Re-marking a teacher replaces their queued entry rather than appending a
// second one: the latest mark is the only one that matters, and the retry
// budget restarts because this is a fresh instruction from the facilitator.
export function queueAttendance(inputs: QueueInput[]): void {
  const now = Date.now();
  const next = [...read()];
  for (const input of inputs) {
    const id = `${input.sessionID}:${input.teacherID}`;
    const existing = next.findIndex((candidate) => candidate.id === id);
    const entry: OutboxEntry = {
      id,
      sessionID: input.sessionID,
      teacherID: input.teacherID,
      teacherName: input.teacherName,
      status: input.status,
      reasonCode: input.reasonCode,
      reasonDetail: input.reasonDetail,
      state: "queued",
      attempts: 0,
      firstQueuedAt: now,
      generation: (existing >= 0 ? next[existing].generation || 0 : 0) + 1,
    };
    if (existing >= 0) next[existing] = entry;
    else next.push(entry);
  }
  write(next);
}

export function retryEntry(id: string): Promise<void> {
  write(
    read().map((entry) =>
      entry.id === id
        ? {
            ...entry,
            state: "queued",
            attempts: 0,
            firstQueuedAt: Date.now(),
            generation: (entry.generation || 0) + 1,
            error: undefined,
          }
        : entry
    )
  );
  return flushOutbox();
}

export function discardEntry(id: string): void {
  write(read().filter((entry) => entry.id !== id));
}

function discardIfCurrent(sent: OutboxEntry): void {
  write(read().filter((entry) => entry.id !== sent.id || entry.generation !== sent.generation));
}

function patch(id: string, generation: number | undefined, changes: Partial<OutboxEntry>): void {
  write(
    read().map((entry) =>
      entry.id === id && entry.generation === generation ? { ...entry, ...changes } : entry
    )
  );
}

// Concurrent callers join the run already in progress rather than getting
// an immediately-resolved promise — awaiting a flush must mean the work is
// actually done, or a row can be left showing "syncing" forever.
let inFlight: Promise<void> | null = null;

export function flushOutbox(): Promise<void> {
  if (inFlight) return inFlight;
  if (!getToken() || !navigator.onLine) return Promise.resolve();
  const due = read().filter((entry) => entry.state !== "failed" && !isCapped(entry));
  if (!due.length) return Promise.resolve();

  inFlight = deliver(due).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function deliver(due: OutboxEntry[]): Promise<void> {
  {
    for (const entry of due) {
      if (
        !read().some(
          (current) => current.id === entry.id && current.generation === entry.generation
        )
      )
        continue;
      patch(entry.id, entry.generation, { state: "syncing" });
      try {
        await request(`/api/facilitator/pd-days/${entry.sessionID}/attendance/${entry.teacherID}`, {
          method: "PUT",
          body: JSON.stringify({
            status: entry.status,
            reason_code: entry.reasonCode || "",
            reason_detail: entry.reasonDetail || "",
          }),
        });
        discardIfCurrent(entry);
        track("attendance_synced", {
          payload: {
            session_id: entry.sessionID,
            teacher_id: entry.teacherID,
            attempts: entry.attempts + 1,
          },
        });
      } catch (cause) {
        const attempts = entry.attempts + 1;
        const message = cause instanceof Error ? cause.message : "Request failed";
        // 409: the mark was already submitted differently; retrying can't help.
        const exhausted =
          (cause instanceof RequestError && cause.status === 409) ||
          attempts >= MAX_ATTEMPTS ||
          Date.now() - entry.firstQueuedAt >= MAX_AGE_MS;
        patch(entry.id, entry.generation, {
          attempts,
          state: exhausted ? "failed" : "queued",
          error: message,
        });
        if (exhausted) {
          track("attendance_sync_failed", {
            payload: {
              session_id: entry.sessionID,
              teacher_id: entry.teacherID,
              attempts,
              status: cause instanceof RequestError ? cause.status : null,
              queued_seconds: Math.round((Date.now() - entry.firstQueuedAt) / 1000),
            },
          });
        }
      }
    }
  }
}

// One shared ticker retries whatever is still within its budget; it stops
// itself once nothing is left, so an idle app holds no timer.
export function startOutboxRetries(): () => void {
  if (!timer) {
    timer = setInterval(() => {
      const live = read().filter((entry) => entry.state !== "failed" && !isCapped(entry));
      if (!live.length) {
        // Mark anything that aged out while offline, then idle.
        const aged = read().map((entry) =>
          entry.state !== "failed" && isCapped(entry)
            ? { ...entry, state: "failed" as const }
            : entry
        );
        if (aged.some((entry, index) => entry.state !== read()[index]?.state)) write(aged);
        return;
      }
      void flushOutbox();
    }, RETRY_INTERVAL_MS);
  }
  return () => {
    if (timer) clearInterval(timer);
    timer = null;
  };
}

window.addEventListener("online", () => void flushOutbox());
